/**
 * Surface textures for characters: which painted cell goes on which part.
 *
 * Two generated 4 × 4 sheets — faces and gear — cut into 32 per-cell WebP files by
 * `grind/scripts/slice-textures.py`. This file is the other half of that: the slicer names
 * files by their **cell address** (`gear-r3c1.webp`) and knows nothing about what they
 * depict, and every statement about what a cell *is* lives in one table here, beside the
 * code that maps it onto a mesh.
 *
 * The address is the only thing written on both sides, which is the property the icon
 * pipeline has for the same reason: there is nowhere for a second spelling to live, so a
 * cell cannot be cut from the wrong art. What a *name* can still be is wrong about the
 * picture, and no amount of tooling catches that — only looking does.
 *
 * ── Why these are textures and the models were flat before ───────────────────
 *
 * `ModelPalette`'s ten flat materials are still there and still the fallback. A texture
 * adds a sampler and a define to the generated material shader, which is exactly the
 * category of thing `?safe=1` exists to switch off — a shader that will not compile takes
 * the whole world black behind a working HUD, so every optional sampler needs a way out.
 * `renderFeatures.textures` is that switch and it already governed the zone's procedural
 * surfaces; characters now ride the same flag.
 *
 * ── A cell is either a swatch or a garment, and it is not a free choice ──────
 *
 * The prompt asked for "flat square seamless texture swatches only" and the sheet came
 * back with eleven of those and five *shaped* cells: the blue tunic has a neckline, three
 * of the four plate cells are cuirasses with armholes and a waist, and one cell is a boot
 * in three-quarter view. They are not mistakes — a cuirass painted as a cuirass is better
 * on a chest than any tiling metal would be — but they can only go where their shape is
 * true. Mapped onto a thigh, the boot's black background becomes a black thigh, and the
 * bronze cuirass puts a second set of armholes on a shin.
 *
 * So `kind` is part of the vocabulary rather than a comment: `SWATCH` may go anywhere,
 * `FRONT` may only go on a part whose front face is what the player sees. Nothing enforces
 * it at runtime — it is a fact about the art that the call sites have to respect, and
 * `atlas.test.ts` holds every mapping in this file to it.
 */

import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Scene } from '@babylonjs/core/scene';

/** Rows and columns, matching `slice-textures.py`. */
export const GRID = 4;

/** A cell address on either sheet, exactly as the slicer names its files. */
export type CellAddress = `r${1 | 2 | 3 | 4}c${1 | 2 | 3 | 4}`;

/**
 * Which generated sheet a cell belongs to. Mirrors `slice-textures.py`'s `SHEETS`.
 *
 * `prop` is here and almost unused — see `PROP`. It is in the union because the files exist and
 * naming it is how the reason for not using them stays written down.
 */
export type Sheet = 'face' | 'gear' | 'scene' | 'weapon' | 'prop' | 'ground';

/**
 * Whether a cell tiles or is a picture of a garment.
 *
 * `SWATCH` — seamless surface, correct on any face of any part.
 * `FRONT` — a shaped object centred in its cell, correct only where the part's front face
 * is the one being read, and wrong anywhere it would repeat or wrap.
 */
export type CellKind = 'SWATCH' | 'FRONT';

export interface Cell {
  sheet: Sheet;
  at: CellAddress;
  kind: CellKind;
  /** What the picture actually is, from having looked at it. */
  depicts: string;
}

function cell(sheet: Sheet, at: CellAddress, kind: CellKind, depicts: string): Cell {
  return { sheet, at, kind, depicts };
}

/**
 * The gear sheet: fabrics, leathers, metals, and belts and trim.
 *
 * Row order is the prompt's, and the rows line up with the three `ArmourClass` values the
 * wire already carries — which is why this art wires onto the character with no new field:
 * row 1 is cloth, row 2 is leather, row 3 is plate, and row 4 dresses the belt and boots
 * every body has regardless of class.
 */
export const GEAR = {
  woolCrimson: cell('gear', 'r1c1', 'SWATCH', 'coarse dark-crimson wool, visible weave'),
  tunicBlue: cell('gear', 'r1c2', 'FRONT', 'faded blue tunic with a stitched neckline'),
  burlap: cell('gear', 'r1c3', 'SWATCH', 'dark burlap sack cloth'),
  robeTattered: cell('gear', 'r1c4', 'FRONT', 'tattered cultist robe, gold-embroidered hem points'),
  leatherWorn: cell('gear', 'r2c1', 'SWATCH', 'worn brown leather, fine scratches'),
  brigandine: cell('gear', 'r2c2', 'SWATCH', 'dark studded brigandine, regular stud grid'),
  leatherBoiled: cell('gear', 'r2c3', 'FRONT', 'boiled black leather cuirass with armholes'),
  fur: cell('gear', 'r2c4', 'SWATCH', 'rough fur and hide trim'),
  chainmail: cell('gear', 'r3c1', 'SWATCH', 'interlocking dark steel mail rings'),
  ironPlate: cell('gear', 'r3c2', 'SWATCH', 'hammered iron plate panel, riveted border'),
  bronzePlate: cell('gear', 'r3c3', 'FRONT', 'dull bronze cuirass with armholes and a waist'),
  obsidianPlate: cell('gear', 'r3c4', 'FRONT', 'dark obsidian cuirass with a purple sheen'),
  belt: cell('gear', 'r4c1', 'FRONT', 'weathered leather belts with brass buckles, in bands'),
  boot: cell('gear', 'r4c2', 'FRONT', 'a mud-splattered dark leather boot'),
  trimGold: cell('gear', 'r4c3', 'FRONT', 'gold cloth trim framing a dark panel'),
  filigreeSilver: cell('gear', 'r4c4', 'SWATCH', 'silver metallic filigree pattern'),
} as const;

/**
 * The face sheet: skins, non-human skins, hair, and eyes and masks.
 *
 * Every cell is a **front**, without exception, and that follows from what a face is: the
 * art is a portrait filling its cell, so it is right on the one forward-facing plane of a
 * head and wrong on the other five. Row 4 is where the emissive cells live.
 */
export const FACE = {
  skinFair: cell('face', 'r1c1', 'FRONT', 'fair human face, light stubble'),
  skinWeathered: cell('face', 'r1c2', 'FRONT', 'tanned weathered face with a scar'),
  skinPale: cell('face', 'r1c3', 'FRONT', 'pale vampiric face, white eyes'),
  skinBronze: cell('face', 'r1c4', 'FRONT', 'dark bronze human face'),
  skullAshen: cell('face', 'r2c1', 'FRONT', 'ashen grey undead skull'),
  skinOrc: cell('face', 'r2c2', 'FRONT', 'mossy green orc face with tusks'),
  skinVoid: cell('face', 'r2c3', 'FRONT', 'pale cultist face with ritual tattoos'),
  skinGargoyle: cell('face', 'r2c4', 'FRONT', 'stone-grey gargoyle face'),
  beardBraided: cell('face', 'r3c1', 'FRONT', 'dark braided beard over a mouth'),
  hairGrey: cell('face', 'r3c2', 'SWATCH', 'coarse grey hair weave'),
  hairRed: cell('face', 'r3c3', 'SWATCH', 'fiery red hair pattern'),
  scalpShaved: cell('face', 'r3c4', 'SWATCH', 'shaved-head stubble'),
  eyesAmber: cell('face', 'r4c1', 'FRONT', 'glowing amber eyes on dark skin'),
  visorVoid: cell('face', 'r4c2', 'FRONT', 'glowing cyan void visor on black'),
  visorIron: cell('face', 'r4c3', 'FRONT', 'iron visor grate, deep shadow behind the slots'),
  jawSkeletal: cell('face', 'r4c4', 'FRONT', 'skeletal jaw painted on dark cloth'),
} as const;

/**
 * The scene sheet: building materials and ground.
 *
 * **The best-behaved sheet of the five** — all sixteen cells came back as genuinely seamless
 * tiling swatches, which is why this is the only one whose cells go on *walls and floors*, where
 * a surface repeats across dozens of tiles and a seam would be visible in a straight line all
 * the way across a room.
 */
export const SCENE = {
  cobbles: cell('scene', 'r1c1', 'SWATCH', 'dark wet cobblestone pavement'),
  dungeonBrick: cell('scene', 'r1c2', 'SWATCH', 'cracked charcoal dungeon stone bricks'),
  granite: cell('scene', 'r1c3', 'SWATCH', 'weathered pale granite wall blocks'),
  mossyStone: cell('scene', 'r1c4', 'SWATCH', 'mossy ancient stone wall'),
  oakBeam: cell('scene', 'r2c1', 'SWATCH', 'aged dark oak beam grain, horizontal'),
  shingles: cell('scene', 'r2c2', 'SWATCH', 'dark wooden roof shingles with moss'),
  pinePlank: cell('scene', 'r2c3', 'SWATCH', 'rough-sawn pine planks, vertical'),
  burntTimber: cell('scene', 'r2c4', 'SWATCH', 'burnt black timber wall'),
  slate: cell('scene', 'r3c1', 'SWATCH', 'weathered blue-grey slate roof tiles'),
  ironGrate: cell('scene', 'r3c2', 'SWATCH', 'rusted iron door grate over black voids'),
  copperPlate: cell('scene', 'r3c3', 'SWATCH', 'tarnished verdigris copper roof plates'),
  ironBoundWood: cell('scene', 'r3c4', 'SWATCH', 'dark iron-reinforced timber wall'),
  mud: cell('scene', 'r4c1', 'SWATCH', 'damp dungeon dirt and mud floor'),
  crackedEarth: cell('scene', 'r4c2', 'SWATCH', 'cracked dry earth with small stones'),
  forestMoss: cell('scene', 'r4c3', 'SWATCH', 'overgrown dark forest moss and leaves'),
  sewerStone: cell('scene', 'r4c4', 'SWATCH', 'sewer stone floor with green sludge'),
} as const;

/**
 * The ground sheet: a floor and a wall per biome, at **512²** where the scene sheet is 128²
 * (2026-09-29). Reported: *the textures are pretty pixelated* — a 128 px cell over an 8-unit tile is
 * 16 px a unit against ~25 on screen, magnified; these are 64, so they are minified and sharp.
 *
 * **Not sliced from a sheet** like the others: sixteen separate Flux generations, one per surface,
 * made seamless by blending each with itself shifted by half (`scripts/seamless-texture.py`,
 * `doc/grind/architecture/GROUND-TEXTURES.md`),
 * and named on the same 4×4 grid so the vocabulary and the loader need nothing new. All `SWATCH`.
 */
export const GROUND = {
  hubFloor: cell('ground', 'r1c1', 'SWATCH', 'worn dark cobblestone street'),
  hubWall: cell('ground', 'r1c2', 'SWATCH', 'dark timber planks bound with rusted iron straps'),
  cryptsFloor: cell('ground', 'r1c3', 'SWATCH', 'large cracked grey crypt flagstones'),
  cryptsWall: cell('ground', 'r1c4', 'SWATCH', 'rough grey dungeon stone blocks'),
  calderaFloor: cell('ground', 'r2c1', 'SWATCH', 'black basalt ground with glowing orange lava cracks'),
  calderaWall: cell('ground', 'r2c2', 'SWATCH', 'rough dark basalt with faint ember cracks'),
  abyssFloor: cell('ground', 'r2c3', 'SWATCH', 'dark violet obsidian slabs with glowing purple veins'),
  abyssWall: cell('ground', 'r2c4', 'SWATCH', 'cobbled black obsidian with purple crystal veins'),
  wastesFloor: cell('ground', 'r3c1', 'SWATCH', 'dry cracked desert earth'),
  wastesWall: cell('ground', 'r3c2', 'SWATCH', 'ochre and orange sandstone blocks'),
  marshFloor: cell('ground', 'r3c3', 'SWATCH', 'wet dark swamp mud with green moss'),
  marshWall: cell('ground', 'r3c4', 'SWATCH', 'mossy green stone wall'),
  thicketFloor: cell('ground', 'r4c1', 'SWATCH', 'forest floor of dead leaves, twigs and roots'),
  thicketWall: cell('ground', 'r4c2', 'SWATCH', 'twisted dark tree roots and bark'),
  cavernsFloor: cell('ground', 'r4c3', 'SWATCH', 'damp blue-grey cave rock with mineral specks'),
  cavernsWall: cell('ground', 'r4c4', 'SWATCH', 'wet dark slate cave rock with pale veins'),
} as const;

/**
 * The weapon sheet: blade, shaft, shield and gem surfaces.
 *
 * **Mixed, and the kinds record which is which.** Rows 1 and 2 are close-ups painted on a
 * diagonal — a blade edge running corner to corner, a shaft crossing the cell — so they read as
 * material on a long thin part and are marked `SWATCH` on that basis rather than because they
 * tile. Row 3 holds two *whole shields* seen face on, which is a `FRONT` and a good one: a
 * shield's face is exactly one flat quad pointed at the camera. Row 4 is gems and orbs on black,
 * which is the one background this game can use as-is.
 */
export const WEAPON = {
  steelEdge: cell('weapon', 'r1c1', 'SWATCH', 'sharp steel blade with scratch highlights'),
  rustedIron: cell('weapon', 'r1c2', 'SWATCH', 'rusted pitted iron cleaver metal'),
  voidBlade: cell('weapon', 'r1c3', 'SWATCH', 'dark steel veined with violet void light'),
  holyGold: cell('weapon', 'r1c4', 'SWATCH', 'golden holy metal cut with glowing runes'),
  oakShaft: cell('weapon', 'r2c1', 'SWATCH', 'carved oak staff grain with rune marks'),
  ebonyShaft: cell('weapon', 'r2c2', 'SWATCH', 'near-black polished ebony wand wood'),
  leatherGrip: cell('weapon', 'r2c3', 'SWATCH', 'crossed leather handle wrapping'),
  ashBow: cell('weapon', 'r2c4', 'SWATCH', 'pale aged ash bow limb wood'),
  woodShield: cell('weapon', 'r3c1', 'FRONT', 'iron-rimmed round wooden shield, face on'),
  steelBoss: cell('weapon', 'r3c2', 'FRONT', 'dark steel round shield with a boss, face on'),
  shieldBacking: cell('weapon', 'r3c3', 'SWATCH', 'worn leather shield backing'),
  runePlate: cell('weapon', 'r3c4', 'FRONT', 'glowing amber rune inscription plate'),
  rubyCore: cell('weapon', 'r4c1', 'FRONT', 'glowing ruby magic crystal on black'),
  lightningCore: cell('weapon', 'r4c2', 'FRONT', 'crackling cyan lightning core on black'),
  voidOrb: cell('weapon', 'r4c3', 'FRONT', 'dark violet void orb on black'),
  poisonRune: cell('weapon', 'r4c4', 'SWATCH', 'green glowing poison runes on dark metal'),
} as const;

/**
 * The prop sheet — **cut and almost entirely unused, deliberately.**
 *
 * The prompt asked for "flat square seamless texture swatches only" and the sheet came back as
 * sixteen **cut-out objects on white**: a barrel, a lantern, a pile of bones, each floating in
 * its own cell with white all around it. That is an *icon* sheet, and it is a perfectly good one
 * — it is simply not a texture atlas, and the difference is not cosmetic. A surface cell is
 * sampled across a whole face; mapped onto a mesh, white background is a **white mesh**, and
 * there is no white anywhere in this game.
 *
 * Three cells are usable because three cells happen to fill their own square: the bench's wood
 * grain, the tavern curtain, and the hay bale. They are named and the rest are not, so that
 * anybody reaching for a barrel finds nothing rather than finding a cell that will paint a white
 * halo around a barrel-shaped box.
 *
 * **The fix is to regenerate the sheet**, not to key the white out. Keying gives cut-outs on
 * transparency, which is the icon pipeline's output and belongs in `slice-icons.py` — and a
 * cut-out barrel is not a barrel *surface*, so it would still be the wrong kind of thing for
 * a mesh. What the props want is what `scene` got: sixteen tiling materials.
 */
export const PROP = {
  benchWood: cell('prop', 'r1c3', 'SWATCH', 'dark tavern bench wood grain, fills its cell'),
  tavernCurtain: cell('prop', 'r2c4', 'SWATCH', 'gold-embroidered curtain, fills its cell'),
  hayBale: cell('prop', 'r4c3', 'SWATCH', 'straw bale, fills its cell'),
} as const;

/**
 * Cells the prop sheet contains that this build refuses to name.
 *
 * Kept as data rather than deleted so the reason survives: these thirteen were **cut** (the
 * files are on disk) and are not in `PROP` because their art is an object on white. If the sheet
 * is regenerated as swatches, this list is the work.
 */
export const PROP_UNUSABLE: readonly CellAddress[] = [
  'r1c1', 'r1c2', 'r1c4', // barrel, cargo crate, bucket — objects on white
  'r2c1', 'r2c2', 'r2c3', // banner, tent canvas, burlap sack — objects on white
  'r3c1', 'r3c2', 'r3c3', 'r3c4', // lantern, candle, lamp post, alchemy jar — objects on white
  'r4c1', 'r4c2', 'r4c4', // bones, books, chains — objects on white
];

/** Every cell in the vocabulary, for tests and for iteration. */
export const ALL_CELLS: readonly Cell[] = [
  ...Object.values(GEAR),
  ...Object.values(FACE),
  ...Object.values(SCENE),
  ...Object.values(WEAPON),
  ...Object.values(PROP),
  ...Object.values(GROUND),
];

/**
 * The cut cells, by `<sheet>-<address>`.
 *
 * `import.meta.glob` rather than 32 import statements, for the reason `ui/iconArt.ts` uses
 * one: it makes the set of available textures **the set of files on disk**, resolved when
 * the bundle is built, so re-running the slicer is the whole of adding a sheet. Here it
 * buys one thing more — a cell with no file is a knowable state, so a model falls back to
 * the flat material it used before rather than asking Babylon to load a 404 and rendering
 * an untextured black mesh while the console fills up.
 */
const FILES = import.meta.glob<string>('../../assets/textures/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

const BY_KEY = new Map<string, string>(
  Object.entries(FILES).map(([path, url]) => [
    path.slice(path.lastIndexOf('/') + 1).replace(/\.webp$/, ''),
    url,
  ]),
);

/** The key a cell's file is stored under. Pure, so the naming rule is testable. */
export function cellKey(c: Cell): string {
  return `${c.sheet}-${c.at}`;
}

/** The URL for a cell, or `undefined` when this build has no file for it. */
export function cellUrl(c: Cell): string | undefined {
  return BY_KEY.get(cellKey(c));
}

/** Every cut cell key, for the guard that catches a glob matching nothing. */
export function cutCellKeys(): string[] {
  return [...BY_KEY.keys()].sort();
}

/**
 * Pixel column and row of a cell, zero-based. Pure.
 *
 * Nothing in the client samples a sheet any more — the cells are separate files — so this
 * exists to hold the *address* arithmetic to the slicer's, which is the one number the two
 * sides share.
 */
export function cellIndex(at: CellAddress): { col: number; row: number } {
  return { row: Number(at[1]) - 1, col: Number(at[3]) - 1 };
}

/**
 * A texture for one cell.
 *
 * **Mipmapped and trilinear**, which is the opposite of `game/fx.ts`'s choice and the
 * reason these are separate files at all. A spell sprite is drawn at roughly the size it
 * was painted; a character is minified hard — a 0.72-unit plate chest is about six output
 * pixels at the gameplay camera, so a 128 px cell arrives ~20× reduced. Without mipmaps
 * that reduction aliases, and the texture crawls as the character walks, which reads worse
 * than the flat colour it replaced. Packed in a sheet, mipmaps could not be used at all:
 * each level averages a wider neighbourhood until a cell is mixing in its neighbours, and
 * `CLAMP_ADDRESSMODE` clamps to the sheet rather than to the cell. Cut apart, every level
 * is correct and the clamp is exact.
 *
 * Clamped on both axes even so: a `SWATCH` would tile happily, but nothing here maps UVs
 * outside 0..1, and clamping means a filter tap at the very edge repeats the edge instead
 * of reaching for the far side of the picture.
 *
 * **A new `Texture` per call, sharing one decoded image.** Babylon caches by URL, so the
 * bytes are fetched and decoded once however many models ask for the same cell — the same
 * arrangement `spriteTexture` relies on, and the reason a shared instance would be wrong
 * is the same too: a `Texture` carries its own sampling state.
 */
export function cellTexture(
  scene: Scene,
  c: Cell,
  opts: { tiling?: boolean; repeat?: number } = {},
): Texture | null {
  const url = cellUrl(c);
  if (!url) return null;

  const tex = new Texture(url, scene, false, false, Texture.TRILINEAR_SAMPLINGMODE);
  // **Tiling is opt-in and only a `SWATCH` may ask for it.** A zone floor sets one texture repeat
  // per world tile, so a room is the same cell laid down forty times and the wrap mode is what
  // makes the seam between two tiles invisible. A character part is the opposite case: nothing
  // maps UVs outside 0..1, so clamping is right and it means a filter tap at the very edge
  // repeats the edge instead of reaching across the picture.
  const mode = opts.tiling ? Texture.WRAP_ADDRESSMODE : Texture.CLAMP_ADDRESSMODE;
  tex.wrapU = mode;
  tex.wrapV = mode;

  // **How many times the cell repeats across whatever it is mapped onto.**
  //
  // The mesh's own UVs put one repeat per world tile, and a tile is eight units — so at `repeat: 1`
  // a cobblestone cell six stones wide made each stone 1.3 units, as wide as the character standing
  // on it. Reported: *the stone floor texture in town is pixelised, the stones are too big.* The
  // "pixelised" half is the same cause: one 128 px cell stretched over eight world units is 16 px
  // per unit against a screen showing ~25, so it was magnified rather than minified.
  //
  // Only meaningful with `tiling`, since a clamped texture has nothing to repeat into.
  if (opts.repeat && opts.repeat !== 1) {
    tex.uScale = opts.repeat;
    tex.vScale = opts.repeat;
  }

  return tex;
}
